import { useEffect, useState } from 'react'
import { Camera, ClipboardCopy, Globe, Inbox, Megaphone, Plus, RefreshCw, Store, Trash2 } from 'lucide-react'

import {
  DEFAULT_AUTO_POST,
  POST_STATUS_LABELS,
  POST_STATUS_VARIANT,
  PROVIDER_LABELS,
  TRIGGER_LABELS,
  type AutoPostFlags,
  type IntegrationConnection,
  type IntegrationPost,
  type ManagedPage,
} from '@/types/integrations'
import {
  useConnections,
  useCreateConnection,
  useCreateFacebookConnection,
  useDeleteConnection,
  useFacebookCallback,
  useIntegrationPosts,
  useStartFacebookConnect,
  useUpdateConnection,
} from '@/features/integrations/hooks'
import { useMe } from '@/features/auth/hooks'
import { QuickBooksConnectCard } from '@/features/quickbooks/components/quickbooks-card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const AUTO_POST_KEYS: { key: keyof AutoPostFlags; label: string }[] = [
  { key: 'new_listing', label: 'New listing' },
  { key: 'price_drop', label: 'Price drop' },
  { key: 'sold', label: 'Sold' },
]

export function IntegrationsTab() {
  return (
    <div className="space-y-6">
      <FacebookConnectCard />
      <LeadWebhookCard />
      <QuickBooksConnectCard />
      <ConnectionsCard />
      <PostsCard />
    </div>
  )
}

// --- Facebook / Instagram connect ---

function FacebookConnectCard() {
  const { data: connections = [] } = useConnections()
  const startConnect = useStartFacebookConnect()
  const callback = useFacebookCallback()
  const createConnection = useCreateFacebookConnection()

  const [picker, setPicker] = useState<{
    token: string
    pages: ManagedPage[]
    simulation: boolean
  } | null>(null)
  const [pageId, setPageId] = useState('')
  const [name, setName] = useState('')
  const [linkInstagram, setLinkInstagram] = useState(true)
  const [syncLeads, setSyncLeads] = useState(true)
  const [autoPost, setAutoPost] = useState<AutoPostFlags>(DEFAULT_AUTO_POST)
  const updateMutation = useUpdateConnection()

  const facebook = connections.find((c) => c.provider === 'facebook_page')

  // Meta redirects back to this page with ?code&state (registered redirect
  // URI). Extracted once at mount; the effect completes the exchange.
  const [oauthParams] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    if (!code || !state) return null
    return { code, state }
  })

  useEffect(() => {
    if (!oauthParams) return
    let cancelled = false
    // Clean the OAuth params out of the address bar immediately so a refresh
    // can't replay the code.
    window.history.replaceState({}, '', window.location.pathname)
    void (async () => {
      try {
        const result = await callback.mutateAsync(oauthParams)
        if (!cancelled && result.pages.length > 0) {
          setPicker({ token: result.selection_token, pages: result.pages, simulation: false })
          setPageId(result.pages[0].id)
          setName(result.pages[0].name)
        }
      } catch {
        // error surfaces via the global mutation toast
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Step 1: start the connect flow. In simulation mode (no Meta app
  // configured) the backend answers with a ready-made selection and the
  // picker opens immediately; otherwise the browser goes to Meta's dialog
  // and Meta redirects back here with ?code&state (handled below).
  const handleConnect = async () => {
    try {
      const start = await startConnect.mutateAsync(undefined)
      if (start.simulation && start.selection_token) {
        setPicker({ token: start.selection_token, pages: start.pages ?? [], simulation: true })
        setPageId(start.pages?.[0]?.id ?? '')
        setName(start.pages?.[0]?.name ?? 'Facebook Page')
        return
      }
      window.location.href = start.url
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  const handleSave = async () => {
    if (!picker || !pageId) return
    try {
      await createConnection.mutateAsync({
        selection_token: picker.token,
        page_id: pageId,
        name: name.trim() || 'Facebook Page',
        link_instagram: linkInstagram,
        sync_leads: syncLeads,
        auto_post: autoPost,
        message_template: '',
      })
      setPicker(null)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  const chosen = picker?.pages.find((p) => p.id === pageId)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" /> Facebook &amp; Instagram
        </CardTitle>
        <CardDescription>
          Auto-post new, repriced, and sold listings to your dealership's Page. Production use
          requires a Meta developer app with App Review (see README); until then connections run in
          simulation mode.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {facebook ? (
          <div className="space-y-2 rounded-lg border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium">{facebook.page_name || facebook.name}</div>
                <div className="text-xs text-muted-foreground">
                  {facebook.ig_username && `Instagram linked: ${facebook.ig_username}`}
                  {!facebook.ig_username && 'No Instagram account linked'}
                  {facebook.token_set ? '' : ' · reconnect required (no token stored)'}
                </div>
              </div>
              <Badge variant={facebook.enabled ? 'secondary' : 'outline'}>
                {facebook.enabled ? 'Active' : 'Paused'}
              </Badge>
            </div>
            <label className="flex items-center gap-2 border-t pt-2 text-sm">
              <Checkbox
                checked={facebook.sync_leads}
                onCheckedChange={(v) =>
                  updateMutation.mutate({
                    id: facebook.id,
                    body: {
                      provider: facebook.provider,
                      name: facebook.name,
                      enabled: facebook.enabled,
                      sync_leads: !!v,
                      auto_post: facebook.auto_post,
                      message_template: facebook.message_template,
                    },
                  })
                }
              />
              Sync leads from Facebook Lead Ads into Marketing
            </label>
          </div>
        ) : (
          <Button onClick={handleConnect} disabled={startConnect.isPending}>
            <Megaphone className="mr-2 h-4 w-4" />
            {startConnect.isPending ? 'Connecting…' : 'Connect Facebook Page'}
          </Button>
        )}
        {startConnect.isError && (
          <p className="text-sm text-destructive">{(startConnect.error as Error).message}</p>
        )}
      </CardContent>

      <Dialog open={!!picker} onOpenChange={(open) => !open && setPicker(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choose your Page</DialogTitle>
            <DialogDescription>
              {picker?.simulation
                ? 'Simulation mode: a demo page is offered because no Meta app is configured.'
                : 'Pick the page FlowPilot should post listings to.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Page</label>
              <div className="space-y-1.5">
                {picker?.pages.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 rounded-md border p-2.5 text-sm"
                  >
                    <input
                      type="radio"
                      name="fb-page"
                      checked={pageId === p.id}
                      onChange={() => {
                        setPageId(p.id)
                        setName(p.name)
                      }}
                    />
                    <span className="flex-1">{p.name}</span>
                    {p.ig_username && (
                      <Badge variant="outline" className="gap-1">
                        <Camera className="h-3 w-3" /> {p.ig_username}
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Connection name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            {chosen?.ig_username && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={linkInstagram} onCheckedChange={(v) => setLinkInstagram(!!v)} />
                Also post to Instagram ({chosen.ig_username})
              </label>
            )}

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={syncLeads} onCheckedChange={(v) => setSyncLeads(!!v)} />
              Sync leads from Facebook Lead Ads into Marketing
            </label>

            <div className="space-y-2">
              <label className="text-sm font-medium">Auto-post</label>
              <div className="flex flex-wrap gap-4">
                {AUTO_POST_KEYS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={autoPost[key]}
                      onCheckedChange={(v) => setAutoPost((f) => ({ ...f, [key]: !!v }))}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicker(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!pageId || createConnection.isPending}>
              {createConnection.isPending ? 'Saving…' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// --- Connections (webhook + marketplace CRUD, FB toggles) ---

function ConnectionsCard() {
  const { data: connections = [], isLoading } = useConnections()
  const updateMutation = useUpdateConnection()
  const deleteMutation = useDeleteConnection()
  const [webhookOpen, setWebhookOpen] = useState(false)

  const setFlags = (c: IntegrationConnection, key: keyof AutoPostFlags, value: boolean) => {
    updateMutation.mutate({
      id: c.id,
      body: {
        provider: c.provider,
        name: c.name,
        enabled: c.enabled,
        auto_post: { ...c.auto_post, [key]: value },
        message_template: c.message_template,
      },
    })
  }

  const toggleEnabled = (c: IntegrationConnection) => {
    updateMutation.mutate({
      id: c.id,
      body: {
        provider: c.provider,
        name: c.name,
        enabled: !c.enabled,
        auto_post: c.auto_post,
        message_template: c.message_template,
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" /> Connections
            </CardTitle>
            <CardDescription>
              Every connected channel and which events auto-post to it.
            </CardDescription>
          </div>
          <Button onClick={() => setWebhookOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add webhook
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? null : connections.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No connections yet.
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Auto-post on</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {PROVIDER_LABELS[c.provider]}
                        {c.webhook_url && ` · ${c.webhook_url}`}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant={c.enabled ? 'secondary' : 'outline'}
                        onClick={() => toggleEnabled(c)}
                      >
                        {c.enabled ? 'Active' : 'Paused'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {c.provider === 'facebook_page' || c.provider === 'webhook' ? (
                        <div className="flex flex-wrap gap-3">
                          {AUTO_POST_KEYS.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-1.5 text-xs">
                              <Checkbox
                                checked={c.auto_post[key]}
                                onCheckedChange={(v) => setFlags(c, key, !!v)}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {c.provider !== 'facebook_page' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMutation.mutate(c.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <WebhookDialog open={webhookOpen} onOpenChange={setWebhookOpen} />
    </Card>
  )
}

function WebhookDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const createMutation = useCreateConnection()
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [template, setTemplate] = useState('')

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setName('Listing syndication')
      setUrl('')
      setSecret('')
      setTemplate('')
    }
  }

  const submit = async () => {
    try {
      await createMutation.mutateAsync({
        provider: 'webhook',
        name: name.trim(),
        url: url.trim(),
        secret: secret.trim() || undefined,
        auto_post: DEFAULT_AUTO_POST,
        message_template: template,
      })
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add webhook</DialogTitle>
          <DialogDescription>
            Listing events POST as signed JSON — the payload carries the boat fields and signed
            photo URLs, with an X-FlowPilot-Signature HMAC header.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Endpoint URL</label>
            <Input
              placeholder="https://syndicator.example.com/hooks/flowpilot"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Signing secret (optional)</label>
            <Input value={secret} onChange={(e) => setSecret(e.target.value)} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Message template (optional)</label>
            <Textarea
              rows={3}
              placeholder="Just in: {{year}} {{make}} {{model}} — {{price}}"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!name.trim() || !url.trim() || createMutation.isPending}
          >
            Add connection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Inbound lead webhook (website forms, Zapier/Make, ...) ---

function LeadWebhookCard() {
  const { data: me } = useMe()
  const { data: connections = [] } = useConnections()
  const createMutation = useCreateConnection()
  const deleteMutation = useDeleteConnection()
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')

  const leadHooks = connections.filter((c) => c.provider === 'lead_webhook')

  const endpointFor = (token?: string) =>
    `${window.location.origin}/api/v1/integrations/leads/webhook/${me?.account_slug ?? ':account'}/${token ?? ':token'}`

  const submit = async () => {
    try {
      await createMutation.mutateAsync({
        provider: 'lead_webhook',
        name: name.trim() || 'Website form leads',
        auto_post: DEFAULT_AUTO_POST,
        message_template: '',
      })
      setCreateOpen(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5" /> Lead webhook
            </CardTitle>
            <CardDescription>
              Receive leads pushed from your website form, Zapier, or any form tool. POST JSON
              (first_name, last_name, email, phone, notes, optional id/source) to the endpoint —
              deliveries land in Marketing → Leads.
            </CardDescription>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add endpoint
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {leadHooks.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No lead endpoints yet — create one and point your form tool at it.
          </div>
        ) : (
          <div className="space-y-2">
            {leadHooks.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-lg border p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{c.name}</span>
                    <Badge variant={c.enabled ? 'secondary' : 'outline'}>
                      {c.enabled ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                  <code className="block truncate text-xs text-muted-foreground">
                    POST {endpointFor(c.webhook_token)}
                  </code>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Copy endpoint"
                  onClick={() => navigator.clipboard?.writeText(endpointFor(c.webhook_token))}
                >
                  <ClipboardCopy className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteMutation.mutate(c.id)}
                  title="Delete endpoint"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add lead endpoint</DialogTitle>
            <DialogDescription>
              A secret URL is generated — paste it into your form tool as the webhook target. Send
              a stable id with each submission to make retries dedupe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// --- Post log ---

function PostsCard() {
  const { data: posts = [], isLoading } = useIntegrationPosts()
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? posts : posts.slice(0, 5)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Store className="h-5 w-5" /> Recent posts
        </CardTitle>
        <CardDescription>
          Every auto and manual listing post, including skips and failures.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? null : posts.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nothing posted yet — posts appear when listings change and a connection is active.
          </div>
        ) : (
          <>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Boat</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((p: IntegrationPost) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.boat_label || '—'}</TableCell>
                      <TableCell className="text-sm">{p.connection}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {TRIGGER_LABELS[p.trigger]}
                      </TableCell>
                      <TableCell>
                        <Badge variant={POST_STATUS_VARIANT[p.status]}>
                          {POST_STATUS_LABELS[p.status]}
                        </Badge>
                        {p.error && (
                          <div className="mt-0.5 max-w-64 text-xs text-muted-foreground">
                            {p.error}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(p.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {posts.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => setShowAll(!showAll)}
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                {showAll ? 'Show less' : `Show all ${posts.length}`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
